import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Draggable from 'react-draggable';

import { withStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';

import IconTextbox from 'components/icons/IconTextbox';
import IconMultiLine from 'components/icons/IconMultiLine';
import IconRadio from 'components/icons/IconRadio';
import IconCheckboxChecked from 'components/icons/IconCheckboxChecked';
import IconDropdown from 'components/icons/IconDropdown';
import IconCalendarEnd from 'components/icons/IconCalendarEnd';
import IconSignature from 'components/icons/IconSignature';
import IconList from 'components/icons/IconList';
import IconImage from 'components/icons/IconImage';

import pxToRem from 'styles/pxToRem';
import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  root: {
    display: 'block',
    position: 'relative',
    padding: 0,
    borderTop: `1px solid ${theme.palette.primary.A100}`,
    '&:hover': {
      zIndex: 1,
    },
  },
  rootDragged: {
    zIndex: 2,
    '& $listItemContentClone': {
      opacity: 0.2,
    },
    '& $listItemContentOriginal': {
      backgroundColor: theme.palette.common.white,
      boxShadow: `0 2px 10px 0 rgba(${hexToRgb(theme.palette.secondary.A100)}, 0.14)`,
      '& $listItemContent': {
        borderColor: theme.palette.primary.A100,
      },
    },
  },
  rootLast: {
    '& $listItemContentOriginal': {
      borderBottomLeftRadius: 10,
    },
    '&$rootDragged $listItemContentOriginal': {
      borderBottomLeftRadius: 0,
    },
  },
  listItemContent: {
    display: 'flex',
    padding: '12px 24px',
  },
  listItemContentClone: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0,
  },
  listItemContentOriginal: {
    cursor: 'pointer',
    '& $listItemContent': {
      margin: -1,
      border: '1px solid transparent',
    },
    '&:hover': {
      zIndex: 2,
      boxShadow: `0 2px 10px 0 rgba(${hexToRgb(theme.palette.secondary.A100)},0.14)`,
    },
  },
  listItemTextPrimary: {
    fontSize: pxToRem(12),
    fontWeight: 700,
    textTransform: 'uppercase',
    color: theme.palette.primary[600],
  },
  listItemTextPrimaryVariantPatientInformation: {
    fontWeight: 400,
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontSize: pxToRem(14),
  },
  listItemIcon: {
    color: theme.palette.primary[600],
    marginRight: 0,
    width: 20,
  },
  iconTextbox: {
    height: 16,
  },
  iconMultiLine: {
    height: 16,
  },
  iconRadio: {
    height: 20,
  },
  iconCheckboxChecked: {
    height: 16,
  },
  iconDropdown: {
    height: 16,
  },
  iconCalendarEnd: {
    height: 20,
  },
  iconSignature: {
    height: 18,
  },
  iconList: {
    height: 10,
    marginTop: 5,
  },
  iconImage: {
    marginTop: -2,
  },
});

class TemplatesCreateEditFormAsideItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dragged: false,
      position: null,
      bounds: null,
    };

    this.onStartDrag = this.onStartDrag.bind(this);
    this.onDrag = this.onDrag.bind(this);
    this.onStopDrag = this.onStopDrag.bind(this);
    this.onDraggableDataChange = this.onDraggableDataChange.bind(this);
  }

  componentDidMount() {
    this.onDraggableDataChange();
  }

  onDraggableDataChange() {
    const { boundsSelector } = this.props;
    if (boundsSelector) {
      const draggableRefDOMRect = this.draggableRef.getBoundingClientRect();
      const boundsDOMRect = document.querySelector(boundsSelector).getBoundingClientRect();
      const left = boundsDOMRect.left - draggableRefDOMRect.left;
      const top = boundsDOMRect.top - draggableRefDOMRect.top;
      const right = boundsDOMRect.width - draggableRefDOMRect.width - left;
      const bottom = (boundsDOMRect.height - draggableRefDOMRect.height) + top;
      this.setState({
        bounds: { left, top, right, bottom },
        width: draggableRefDOMRect.width,
        height: draggableRefDOMRect.height,
      });
    }
  }

  onStartDrag(event, data) {
    const { onStartDrag, variant } = this.props;
    const { bounds, width, height } = this.state;
    this.onDraggableDataChange();
    this.setState({ dragged: true });
    if (onStartDrag) {
      onStartDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        variant,
      });
    }
  }

  onDrag(event, data) {
    const { onDrag, variant } = this.props;
    const { bounds, width, height } = this.state;
    if (onDrag) {
      onDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        variant,
      });
    }
  }

  onStopDrag(event, data) {
    const { onStopDrag, variant } = this.props;
    const { bounds, width, height } = this.state;
    this.setState({
      dragged: false,
      position: {
        x: 0,
        y: 0,
      },
    });
    if (onStopDrag) {
      onStopDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        variant,
      });
    }
  }

  render() {
    const {
      variant,
      primaryText,
      last,
      classes,
    } = this.props;
    const { dragged, position, bounds } = this.state;
    let icon;
    switch (variant) {
      case 'Textbox':
        icon = (<IconTextbox className={classes.iconTextbox} />);
        break;
      case 'MultiLine':
        icon = (<IconMultiLine className={classes.iconMultiLine} />);
        break;
      case 'Radio':
        icon = (<IconRadio className={classes.iconRadio} />);
        break;
      case 'Checkbox':
        icon = (<IconCheckboxChecked className={classes.iconCheckboxChecked} />);
        break;
      case 'DropDown':
        icon = (<IconDropdown className={classes.iconDropdown} />);
        break;
      case 'Date':
        icon = (<IconCalendarEnd className={classes.iconCalendarEnd} />);
        break;
      case 'Signature':
        icon = (<IconSignature className={classes.iconSignature} />);
        break;
      case 'Company Logo':
        icon = (<IconImage className={classes.iconImage} />);
        break;
      case 'Patient General':
      case 'Patient Insurance':
      case 'Patient Vitals':
      case 'Patient Prescribers':
      case 'Patient Diagnosis':
      case 'HCPCS Codes':
      case 'Company Information':
        icon = (<IconList className={classes.iconList} />);
        break;
      default:
        icon = '';
    }
    const listItemContent = (
      <div className={classes.listItemContent}>
        <ListItemIcon className={classes.listItemIcon}>
          {icon}
        </ListItemIcon>
        <ListItemText
          classes={{
            primary: classNames(classes.listItemTextPrimary, {
              [classes.listItemTextPrimaryVariantPatientInformation]:
              ['Patient General', 'Patient Insurance', 'Patient Vitals', 'Patient Prescribers', 'Patient Diagnosis', 'HCPCS Codes', 'Company Information', 'Company Logo']
                .indexOf(variant) !== -1,
            }),
          }}
          primary={primaryText}
        />
      </div>
    );
    return (
      <ListItem
        className={classNames(classes.root, {
          [classes.rootDragged]: dragged,
          [classes.rootLast]: last,
        })}
      >
        <div
          className={classes.listItemContentClone}
          role="presentation"
        >
          {listItemContent}
        </div>
        <Draggable
          {...bounds ? { bounds } : {}}
          {...position ? { position } : {}}
          onStart={this.onStartDrag}
          onDrag={this.onDrag}
          onStop={this.onStopDrag}
        >
          <div
            ref={(draggableRef) => {
              this.draggableRef = draggableRef;
            }}
            className={classes.listItemContentOriginal}
          >
            {listItemContent}
          </div>
        </Draggable>
      </ListItem>
    );
  }
}

TemplatesCreateEditFormAsideItem.propTypes = {
  primaryText: PropTypes.string.isRequired,
  variant: PropTypes.oneOf([
    'Textbox',
    'MultiLine',
    'Radio',
    'Checkbox',
    'DropDown',
    'Date',
    'Signature',
    'Patient General',
    'Patient Insurance',
    'Patient Vitals',
    'Patient Prescribers',
    'Patient Diagnosis',
    'HCPCS Codes',
    'Company Information',
    'Company Logo',
  ]).isRequired,
  onStartDrag: PropTypes.func,
  onDrag: PropTypes.func,
  onStopDrag: PropTypes.func,
  boundsSelector: PropTypes.string,
  last: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootDragged: PropTypes.string.isRequired,
    rootLast: PropTypes.string.isRequired,
    listItemContent: PropTypes.string.isRequired,
    listItemContentClone: PropTypes.string.isRequired,
    listItemContentOriginal: PropTypes.string.isRequired,
    listItemIcon: PropTypes.string.isRequired,
    iconTextbox: PropTypes.string.isRequired,
    iconMultiLine: PropTypes.string.isRequired,
    iconRadio: PropTypes.string.isRequired,
    iconCheckboxChecked: PropTypes.string.isRequired,
    iconDropdown: PropTypes.string.isRequired,
    iconCalendarEnd: PropTypes.string.isRequired,
    iconSignature: PropTypes.string.isRequired,
    iconList: PropTypes.string.isRequired,
    listItemTextPrimary: PropTypes.string.isRequired,
    listItemTextPrimaryVariantPatientInformation: PropTypes.string.isRequired,
  }).isRequired,
};

TemplatesCreateEditFormAsideItem.defaultProps = {
  boundsSelector: null,
  onStartDrag: null,
  onDrag: null,
  onStopDrag: null,
  last: false,
};

export default withStyles(styles)(TemplatesCreateEditFormAsideItem);



// WEBPACK FOOTER //
// ./src/components/settings/TemplatesCreateEditForm/TemplatesCreateEditFormAsideItem.js